import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { secondAxios } from "../../../../api/axios";

const emptyForm = { name: "", prefix: "" };

export default function ClientsCrudModal({ open, onClose, onClientsChange }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // create | edit
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await secondAxios.get("/client");
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
      showSnackbar("Failed to load clients", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchClients();
    }
  }, [open, fetchClients]);

  const notifyParent = async () => {
    await fetchClients();
    if (onClientsChange) {
      onClientsChange();
    }
  };

  const handleOpenCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const handleOpenEdit = (client) => {
    setFormMode("edit");
    setEditingId(client.id);
    setForm({
      name: client.name || "",
      prefix: client.prefix || "",
    });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (field, value) => {
    const next =
      field === "prefix"
        ? value.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
        : value.trimStart();
    setForm((prev) => ({ ...prev, [field]: next }));
  };

  const handleSave = async () => {
    const name = form.name.trim();
    const prefix = form.prefix.trim().toLowerCase();
    if (!name || !prefix) {
      showSnackbar("Name and prefix are required", "error");
      return;
    }

    try {
      setSaving(true);
      if (formMode === "create") {
        await secondAxios.post("/client", { name, prefix });
        showSnackbar("Client created");
      } else {
        await secondAxios.patch(`/client/${editingId}`, { name, prefix });
        showSnackbar("Client updated");
      }
      handleCloseForm();
      await notifyParent();
    } catch (err) {
      const status = err?.response?.status;
      const message =
        status === 409
          ? "Client name or prefix already exists"
          : err?.response?.data?.message ||
            `Failed to ${formMode === "create" ? "create" : "update"} client`;
      showSnackbar(
        Array.isArray(message) ? message.join(", ") : message,
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await secondAxios.delete(`/client/${deleteTarget.id}`);
      showSnackbar("Client deleted");
      setDeleteTarget(null);
      await notifyParent();
    } catch (err) {
      const status = err?.response?.status;
      const message =
        status === 409
          ? "Cannot delete client while projects still reference it"
          : err?.response?.data?.message || "Failed to delete client";
      showSnackbar(
        Array.isArray(message) ? message.join(", ") : message,
        "error",
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "prefix",
        header: "Prefix",
        size: 100,
      },
      {
        id: "plantsCount",
        header: "Projects",
        size: 90,
        accessorFn: (row) =>
          Array.isArray(row.plants) ? row.plants.length : 0,
        Cell: ({ cell }) => <span>{cell.getValue()}</span>,
      },
    ],
    [],
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Manage Clients</DialogTitle>
        <DialogContent dividers>
          <MaterialReactTable
            data={clients}
            columns={columns}
            state={{ isLoading: loading }}
            enableRowActions
            positionActionsColumn="last"
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            initialState={{ density: "compact" }}
            renderTopToolbarCustomActions={() => (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
              >
                Add Client
              </Button>
            )}
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenEdit(row.original)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteTarget(row.original)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {formMode === "create" ? "Create Client" : "Edit Client"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              fullWidth
              required
              placeholder="chinook"
              autoFocus
            />
            <TextField
              label="Prefix"
              value={form.prefix}
              onChange={(e) => handleFormChange("prefix", e.target.value)}
              fullWidth
              required
              placeholder="cdt"
              helperText="Used as the project name prefix (e.g. CDT-p001)"
              inputProps={{ maxLength: 20 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !form.name.trim() || !form.prefix.trim()}
          >
            {formMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete client{" "}
            <strong>{deleteTarget?.name}</strong>
            {deleteTarget?.prefix ? ` (${deleteTarget.prefix})` : ""}? This only
            works if no projects are linked to it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
